import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

export const SHIFT_MASK = 1;
export const CTRL_MASK = 2;
export const ALT_MASK = 4;

export type Comparator = (o1: any, o2: any) => boolean;

export default class Utils {
    public static isString(x) {
        return Object.prototype.toString.call(x) === '[object String]';
    }

    public static deepCopy<T>(value: T): T {
        return cloneDeep(value);
    }

    public static ifExistsElse<T>(value: T, defaultValue: T): T {
        return value !== undefined && value !== null ? value : defaultValue;
    }

    public static isExists<T>(value: T): boolean {
        return value !== undefined && value !== null;
    }

    public static isNotExists<T>(value: T): boolean {
        return value === undefined || value === null;
    }

    public static isEmptyString(value: string): boolean {
        return !(value && value.length > 0);
    }

    public static isNotEmptyString(value: string): boolean {
        return value && value.length > 0;
    }

    public static isInList<T>(value: T, ...list: T[]): boolean {
        return list.indexOf(value) >= 0;
    }

    public static firstNonNull<T>(...list: T[]) {
        for (const obj of list) {
            if (Utils.isExists(obj)) return obj;
        }
        return null;
    }

    /**
     * Convert any string value to a lowcase and replace spaces by a dash (eg: "Flight List" => "flight-list")
     * @param value
     */
    public static toLowerCaseKey(value: string) {
        return value.replace(/\s+/g, '-').toLowerCase();
    }

    public static isEquals(obj1: any, obj2: any): boolean {
        if (obj1 === obj2) return true;
        //added this condition because if both are null/undefined they are equals and is not "caught" by the previous condition
        if (Utils.isNotExists(obj1)) {
            return Utils.isNotExists(obj2);
        } else if (Utils.isNotExists(obj2)) {
            return false;
        }
        return isEqual(obj1, obj2);
    }

    public static isNotEquals(d1: object, d2: object): boolean {
        return !this.isEquals(d1, d2);
    }

    public static isEqualsAndNotNull(d1: any, d2: any): boolean {
        if (!d1 || !d2) return false;
        if (d1 === d2) return true;
        return this.isEquals(d1, d2);
    }

    public static isShallowEquals(obj1: object, obj2: object): boolean {
        if (obj1 === obj2) return true;
        if (!obj1 || !obj2) return false;

        if (Array.isArray(obj1) && Array.isArray(obj2)) {
            if (obj1.length !== obj2.length) return false;
            for (let i = 0; i < obj1.length; i++) {
                if (obj1[i] !== obj2[i]) return false;
            }
            return true;
        } else {
            const keys1 = Object.keys(obj1);
            const keys2 = Object.keys(obj2);

            if (keys1.length !== keys2.length) return false;

            for (const key of keys1) {
                if (obj1[key] !== obj2[key] || !Object.prototype.hasOwnProperty.call(obj2, key)) return false;
            }
            return true;
        }
    }

    public static isNotShallowEquals(obj1: object, obj2: object): boolean {
        return !Utils.isShallowEquals(obj1, obj2);
    }

    public static toBooleanValue(b: boolean): boolean {
        return b || false;
    }

    //TODO: collections utils?
    public static isEmpty(array: Array<any>): boolean {
        return !Utils.isNotEmpty(array);
    }

    public static isNotEmpty(array: Array<any>): boolean {
        return array && array.length > 0;
    }

    public static size<T>(array: Array<T>): number {
        return array ? array.length : 0;
    }

    /**
     * Return elements of otherArray that are not contained in array
     * @param array
     * @param otherArray
     */
    public static calcNotInCollection<T extends object>(array: Array<T>, otherArray: Array<T>): Array<T> {
        return otherArray.filter(item => array.indexOf(item) < 0);
    }

    public static contains<T>(array: Array<T>, value: T, comparator: Comparator = Utils.isEquals) {
        for (const elem of array) {
            if (comparator(elem, value)) {
                return true;
            }
        }
        return false;
    }

    public static addIfNeeded<T>(array: Array<T>, value: T) {
        if (!Utils.contains(array, value)) {
            array.push(value);
        }
    }
    public static addAllIfNeeded<T>(array: T[], values: T[]) {
        if (values) {
            for (const value of values) {
                if (!Utils.contains(array, value)) {
                    array.push(value);
                }
            }
        }
    }

    public static copyAndAddIfNeeded<T>(array: Array<T>, value: T): Array<T> {
        if (!Utils.contains(array, value)) {
            const copy = array.slice();
            copy.push(value);
            return copy;
        }
        return array;
    }

    public static copyAndRemoveIfNeeded<T>(array: Array<T>, value: T): Array<T> {
        const idx = array.findIndex(elem => Utils.isEquals(elem, value));
        if (idx === -1) return array;

        const copy = array.slice();
        copy.splice(idx, 1);
        return copy;
    }

    public static copyAndRemoveAllIfNeeded<T>(array: Array<T>, toRemove: Array<T>): Array<T> {
        if (!toRemove || !array) return array;
        const reply = array.filter(elem => !toRemove.includes(elem));
        return array.length === reply.length ? array : reply; // only return the new array if there was a change
    }

    public static copyAndAddAllIfNeeded<T>(array: Array<T>, toAdd: Array<T>): Array<T> {
        if (Utils.isEmpty(toAdd)) return array;
        if (Utils.isEmpty(array)) return toAdd.slice();
        let copy: Array<T> = null;
        for (const el of toAdd) {
            if (!Utils.contains(array, el)) {
                if (copy === null) {
                    copy = array.slice();
                }
                copy.push(el);
            }
        }
        return copy ? copy : array;
    }

    // TODO: StringUtils?
    public static split(string: string, separators: string, nullIfEmpty: boolean = false): Array<string> {
        if (string) {
            const trimStr = string.trim();
            if (trimStr.length > 0) {
                return trimStr.split(new RegExp('[' + separators + ']+', 's'));
            }
        }
        return nullIfEmpty ? null : [];
    }

    public static concat(separator: string, values: string[]) {
        if (values) {
            return values.filter(v => v).join(separator); // Removes null values
        }
    }

    public static compareStrNumbers(a: string, b: string): number {
        const v1 = parseInt(a, 10);
        const v2 = parseInt(b, 10);
        if (isNaN(v1)) return isNaN(v2) ? 0 : 1;
        if (isNaN(v2)) return -1;
        return v1 - v2;
    }
    /**
     * Convert the integer to a String, with leading characters (e.g. space).<br>
     * If the value is negative then we permit an extra character for the minus sign
     * (thus ensuring all the actually digits are aligned)<br>
     * @param value
     * @param numSpaces
     * @return
     */
    public static leftPad(value: number, size: number, padding: string): string {
        const str = Math.abs(value).toString().padStart(size, padding);
        return value < 0 ? '-' + str : str;
    }

    public static leftPadSpace(value: number, size: number): string {
        return this.leftPad(value, size, ' ');
    }

    public static leftPadZero(value: number, size: number): string {
        return this.leftPad(value, size, '0');
    }
    public static padEnd(str: string, len: number, fillStr?: string): string {
        return Utils.ifExistsElse(str, '').padEnd(len, fillStr);
    }

    public static padStart(str: string, len: number, fillStr?: string): string {
        return Utils.ifExistsElse(str, '').padStart(len, fillStr);
    }

    public static toSignedString(value: number, fractionDigits?: number) {
        return (value > 0 ? '+' : '') + value.toFixed(fractionDigits);
    }

    /**
     * convert MY_EXAMPLE to myExample
     * @param str
     */
    public static fromUpperCaseToCamelCase(str: string): string {
        function conv(word: string, idx) {
            if (word && word.length > 0) {
                if (idx === 0) return word.toLowerCase();
                else return word[0].toUpperCase() + word.substring(1).toLowerCase();
            } else {
                return word;
            }
        }
        return str.split('_').map(conv).join('');
    }

    public static getArrayValues<T>(array: { [key: string]: T }): T[] {
        const values = [];
        for (const key in array) {
            values.push(array[key]);
        }
        return values;
    }

    public static mapToObject<T>(map: Map<string, T>, mapFunction?: (value: T) => T): { [key: string]: T } {
        let object: { [key: string]: T };
        if (map) {
            object = {};
            map.forEach((value, key) => {
                object[key] = mapFunction ? mapFunction(value) : value;
            });
        }
        return object;
    }

    public static toUpperCase(value: string): string {
        return value && value.toUpperCase();
    }

    public static removeDuplicates(elements: Array<string>) {
        return elements.filter((object, index, self) => index === self.findIndex(object2 => object === object2));
    }

    public static bitMask(shiftKey: boolean, ctrlKey: boolean, altKey: boolean) {
        return (shiftKey ? SHIFT_MASK : 0) + (ctrlKey ? CTRL_MASK : 0) + (altKey ? ALT_MASK : 0);
    }

    public static isSimpleWildCardEquals(wildCardString: string, value: string): boolean {
        if (wildCardString === value) {
            return true;
        } else if (wildCardString.endsWith('*')) {
            const count = wildCardString.length - 1;
            if (value.length < count) return false;
            for (let i = 0; i < count; i++) {
                if (wildCardString.charAt(i) !== value.charAt(i)) return false;
            }
            return true;
        } else {
            return false;
        }
    }

    public static wildcardToRegex(wildcardString: string): RegExp {
        // Replace the wildcard * with its regex form .* which matches any character any number of time,
        // Adds "^" to enforce exact matching with the beginning of input
        // Add ".*" to match any character at the end of input
        // Doesn't handle special case when string has special characters not compliant with regex (<([{\^-=$!|]})?*+.>)
        const regex = wildcardString.replaceAll('*', '.*');
        return new RegExp(`^${regex}.*`);
    }
}
